import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { FileSpreadsheet, FileText, Plus, Filter, Edit, Trash2, TrendingUp, TrendingDown, Wallet, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { formatCurrencyTND } from '../utils/formatCurrency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CashBook = () => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState({ count: 0, total_income: 0, total_expense: 0, balance: 0 });
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('income'); // 'income' or 'expense'
  const [editingEntry, setEditingEntry] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    reference_number: '',
    category: '',
    amount: '',
    notes: ''
  });

  const incomeCategories = [
    'Bareinzahlung',
    'Kundenzahlung Bar',
    'Bankabhebung',
    'Eigenkapital',
    'Sonstige Einnahme'
  ];

  const expenseCategories = [
    'Mitarbeiterzahlung',
    'Lieferantenrechnung',
    'Büromaterial',
    'Miete',
    'Bankeinzahlung',
    'Gehalt',
    'Transport',
    'Sonstige Ausgabe'
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [startDate, endDate, categoryFilter]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/cashbook/categories`);
      setCategories([...new Set([...incomeCategories, ...expenseCategories, ...response.data])]);
    } catch (error) {
      setCategories([...incomeCategories, ...expenseCategories]);
    }
  };

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      
      const response = await axios.get(`${API}/cashbook?${params.toString()}`);
      setEntries(response.data.entries);
      setSummary(response.data.summary);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      
      const response = await axios.get(`${API}/cashbook/export/excel?${params.toString()}`, {
        responseType: 'blob',
        timeout: 30000
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Kassenbuch_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('reports.exportSuccess'));
    } catch (error) {
      toast.error(t('reports.exportError'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      
      const response = await axios.get(`${API}/cashbook/export/pdf?${params.toString()}`, {
        responseType: 'blob',
        timeout: 30000
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { 
        type: 'application/pdf' 
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Kassenbuch_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('reports.exportSuccess'));
    } catch (error) {
      toast.error(t('reports.exportError'));
    } finally {
      setExporting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      description: '',
      reference_number: '',
      category: '',
      amount: '',
      notes: ''
    });
    setEditingEntry(null);
  };

  const handleOpenDialog = (type, entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      const entryDate = entry.date ? new Date(entry.date).toISOString().split('T')[0] : '';
      const isIncome = entry.income > 0;
      setDialogType(isIncome ? 'income' : 'expense');
      setFormData({
        date: entryDate,
        description: entry.description || '',
        reference_number: entry.reference_number || '',
        category: entry.category || '',
        amount: isIncome ? entry.income : entry.expense,
        notes: entry.notes || ''
      });
    } else {
      setDialogType(type);
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        date: new Date(formData.date).toISOString(),
        description: formData.description,
        reference_number: formData.reference_number || null,
        category: formData.category || null,
        income: dialogType === 'income' ? parseFloat(formData.amount) || 0 : 0,
        expense: dialogType === 'expense' ? parseFloat(formData.amount) || 0 : 0,
        notes: formData.notes || null
      };

      if (editingEntry) {
        await axios.put(`${API}/cashbook/${editingEntry.id}`, data);
        toast.success(t('cashbook.updateSuccess'));
      } else {
        await axios.post(`${API}/cashbook`, data);
        toast.success(dialogType === 'income' ? t('cashbook.depositSuccess') : t('cashbook.withdrawalSuccess'));
      }
      
      setDialogOpen(false);
      resetForm();
      fetchEntries();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/cashbook/${entryToDelete.id}`);
      toast.success(t('cashbook.deleteSuccess'));
      fetchEntries();
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const currentCategories = dialogType === 'income' ? incomeCategories : expenseCategories;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-heading font-bold text-xl sm:text-2xl tracking-tight text-slate-900">
              {t('cashbook.title')}
            </h2>
            <p className="text-slate-500 text-sm">{t('cashbook.subtitle')}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button 
              onClick={() => handleOpenDialog('income')}
              className="w-full sm:w-auto rounded-sm bg-emerald-600 hover:bg-emerald-700 shadow-none"
            >
              <ArrowUpCircle className="w-4 h-4 mr-2" />
              {t('cashbook.deposit')}
            </Button>
            <Button 
              onClick={() => handleOpenDialog('expense')}
              className="w-full sm:w-auto rounded-sm bg-red-600 hover:bg-red-700 shadow-none"
            >
              <ArrowDownCircle className="w-4 h-4 mr-2" />
              {t('cashbook.withdrawal')}
            </Button>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <Button 
            onClick={handleExportExcel}
            disabled={exporting || entries.length === 0}
            variant="outline"
            className="w-full sm:w-auto rounded-sm shadow-none"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {t('reports.exportExcel')}
          </Button>
          <Button 
            onClick={handleExportPDF}
            disabled={exporting || entries.length === 0}
            variant="outline"
            className="w-full sm:w-auto rounded-sm shadow-none"
          >
            <FileText className="w-4 h-4 mr-2" />
            {t('reports.exportPdf')}
          </Button>
        </div>
      </div>

      {/* Current Balance Card - Prominent */}
      <Card className="border-2 border-slate-300 shadow-sm rounded-sm bg-gradient-to-r from-slate-50 to-white">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm uppercase tracking-wider text-slate-500 font-medium">{t('cashbook.currentBalance')}</p>
              <p className={`font-mono text-4xl font-bold mt-1 ${summary.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatCurrencyTND(summary.balance)}
              </p>
            </div>
            <Wallet className="w-12 h-12 text-slate-300" />
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="border-slate-200 shadow-none rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="w-4 h-4" />
            {t('common.filter')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{t('reports.startDate')}</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.endDate')}</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('cashbook.category')}</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('cashbook.allCategories')}</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-slate-200 shadow-none rounded-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-slate-400" />
              <p className="text-xs uppercase tracking-wider text-slate-500">{t('cashbook.entries')}</p>
            </div>
            <p className="font-mono text-2xl font-bold text-slate-900 mt-1">{summary.count}</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50 shadow-none rounded-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              <p className="text-xs uppercase tracking-wider text-emerald-600">{t('cashbook.totalDeposits')}</p>
            </div>
            <p className="font-mono text-2xl font-bold text-emerald-600 mt-1">{formatCurrencyTND(summary.total_income)}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50 shadow-none rounded-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-red-500" />
              <p className="text-xs uppercase tracking-wider text-red-600">{t('cashbook.totalWithdrawals')}</p>
            </div>
            <p className="font-mono text-2xl font-bold text-red-600 mt-1">{formatCurrencyTND(summary.total_expense)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Entries Table */}
      <Card className="border-slate-200 shadow-none rounded-sm">
        <CardHeader>
          <CardTitle className="text-base">{t('cashbook.entryList')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">{t('common.loading')}</div>
          ) : entries.length === 0 ? (
            <div className="text-center py-8 text-slate-400">{t('cashbook.noEntries')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 font-medium text-slate-600">{t('cashbook.date')}</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">{t('cashbook.reference')}</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">{t('cashbook.description')}</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">{t('cashbook.category')}</th>
                    <th className="text-right py-3 px-2 font-medium text-emerald-600">{t('cashbook.deposit')}</th>
                    <th className="text-right py-3 px-2 font-medium text-red-600">{t('cashbook.withdrawal')}</th>
                    <th className="text-right py-3 px-2 font-medium text-slate-600">{t('cashbook.balance')}</th>
                    <th className="text-right py-3 px-2 font-medium text-slate-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-2 font-mono text-slate-900">{formatDate(entry.date)}</td>
                      <td className="py-3 px-2 text-slate-600">{entry.reference_number || '-'}</td>
                      <td className="py-3 px-2 text-slate-700">{entry.description}</td>
                      <td className="py-3 px-2 text-slate-600">{entry.category || '-'}</td>
                      <td className="py-3 px-2 text-right font-mono text-emerald-600 font-medium">
                        {entry.income > 0 ? `+${formatCurrencyTND(entry.income)}` : '-'}
                      </td>
                      <td className="py-3 px-2 text-right font-mono text-red-600 font-medium">
                        {entry.expense > 0 ? `-${formatCurrencyTND(entry.expense)}` : '-'}
                      </td>
                      <td className={`py-3 px-2 text-right font-mono font-bold ${entry.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrencyTND(entry.balance)}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(entry.income > 0 ? 'income' : 'expense', entry)}
                            className="h-8 w-8 rounded-sm hover:bg-slate-100"
                          >
                            <Edit className="w-4 h-4 text-slate-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEntryToDelete(entry);
                              setDeleteDialogOpen(true);
                            }}
                            className="h-8 w-8 rounded-sm hover:bg-red-100"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${dialogType === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
              {dialogType === 'income' ? (
                <>
                  <ArrowUpCircle className="w-5 h-5" />
                  {editingEntry ? t('cashbook.editDeposit') : t('cashbook.newDeposit')}
                </>
              ) : (
                <>
                  <ArrowDownCircle className="w-5 h-5" />
                  {editingEntry ? t('cashbook.editWithdrawal') : t('cashbook.newWithdrawal')}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {dialogType === 'income' 
                ? t('cashbook.depositDescription')
                : t('cashbook.withdrawalDescription')}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">{t('cashbook.date')} *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reference">{t('cashbook.reference')}</Label>
                <Input
                  id="reference"
                  value={formData.reference_number}
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                  placeholder="z.B. K-001"
                  className="rounded-sm"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount" className={dialogType === 'income' ? 'text-emerald-600' : 'text-red-600'}>
                {t('cashbook.amount')} (€) *
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                placeholder="0.00"
                className={`rounded-sm font-mono text-lg ${dialogType === 'income' ? 'border-emerald-300 focus:border-emerald-500' : 'border-red-300 focus:border-red-500'}`}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t('cashbook.description')} *</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                placeholder={dialogType === 'income' 
                  ? t('cashbook.depositPlaceholder')
                  : t('cashbook.withdrawalPlaceholder')}
                className="rounded-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">{t('cashbook.category')}</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder={t('cashbook.selectCategory')} />
                </SelectTrigger>
                <SelectContent>
                  {currentCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">{t('cashbook.notes')}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('cashbook.notesPlaceholder')}
                className="rounded-sm"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}
                className="rounded-sm"
              >
                {t('common.cancel')}
              </Button>
              <Button 
                type="submit" 
                className={`rounded-sm ${dialogType === 'income' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {editingEntry ? t('common.save') : (dialogType === 'income' ? t('cashbook.addDeposit') : t('cashbook.addWithdrawal'))}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('cashbook.deleteEntry')}?</AlertDialogTitle>
            <AlertDialogDescription>
              {t('cashbook.deleteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-sm bg-primary hover:bg-[#D62839]">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CashBook;
